"use client";

import { Control, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { format, isAfter, parseISO, startOfToday } from "date-fns";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import GenericLoader from "@/components/GenericLoader";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";

type StudentOption = { id: string; full_name: string; avatar_url: string | null };

interface PayerStepProps {
  control: Control<any>;
}

export function PayerStep({ control }: PayerStepProps) {
  const { t, i18n } = useTranslation(["finances", "common"]);
  const selectedLessonId = useWatch({ control, name: "lesson_id" });
  const formContext = useFormContext();

  const [students, setStudents] = useState<StudentOption[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [studentsQuery, setStudentsQuery] = useState("");

  const fetchStudents = useDebouncedCallback(async (query: string, lessonId?: string) => {
    setIsLoadingStudents(true);
    try {
      const params: Record<string, string> = {};

      if (lessonId) params.lesson_id = lessonId;
      if (query.trim()) params.search = query.trim();
      const { data } = await axiosApiInstance.get(API_ENDPOINTS.TUTOR.FINANCES.PAYMENTS_STUDENTS, {
        params,
      });

      setStudents(data.data || []);
    } catch (e) {
      setStudents([]);
    } finally {
      setIsLoadingStudents(false);
    }
  }, 500);

  useEffect(() => {
    fetchStudents(studentsQuery, selectedLessonId || undefined);
  }, [studentsQuery, selectedLessonId, fetchStudents]);

  return (
    <div className="flex h-full flex-col px-6 py-8 max-sm:px-4 max-sm:py-6">
      {/* Header */}
      <div className="mb-8 text-center max-sm:mb-6">
        <h1 className="text-accent-foreground mb-3 flex items-center justify-center gap-3 text-2xl font-bold max-sm:text-xl">
          <Icon
            className="text-muted-foreground"
            height="1.75rem"
            icon="tabler:users"
            width="1.75rem"
          />
          {t("finances:createPayment.section.payer")}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-md text-base max-sm:text-sm">
          {t("finances:createPayment.section.payerDesc")}
        </p>
      </div>

      {/* Content */}
      <div className="mx-auto w-full max-w-3xl flex-1 space-y-6">
        {/* Student Selection */}
        <div className="space-y-3">
          <FormField
            control={control}
            name="payer_user_id"
            render={({ field }) => (
              <FormItem>
                <div className="space-y-4">
                  <div className="relative">
                    <Icon
                      className="text-muted-foreground absolute top-1/2 left-4 -translate-y-1/2"
                      height="1.25rem"
                      icon="tabler:search"
                      width="1.25rem"
                    />
                    <Input
                      className="bg-muted/30 focus:bg-muted/20 h-12 rounded-xl pr-4 pl-12 text-base"
                      placeholder={t("finances:createPayment.studentSearchPlaceholder")}
                      value={studentsQuery}
                      onChange={(e) => setStudentsQuery(e.target.value)}
                    />
                  </div>
                  {isLoadingStudents ? (
                    <div className="relative h-54">
                      <GenericLoader className={"absolute rounded-xl"} />
                    </div>
                  ) : (
                    <div className="bg-muted/30 rounded-xl p-3">
                      {students.length > 0 ? (
                        <ScrollArea className="h-54">
                          <div className="space-y-3">
                            {students.map((student, index) => (
                              <motion.button
                                key={student.id}
                                animate={{ opacity: 1, y: 0 }}
                                className={`group relative w-full overflow-hidden rounded-lg p-3 text-left ${
                                  field.value === student.id
                                    ? "bg-accent-foreground shadow-sm"
                                    : "bg-muted/75 hover:bg-muted/50 hover:shadow-sm"
                                }`}
                                initial={{ opacity: 0, y: 10 }}
                                transition={{
                                  duration: 0.2,
                                  delay: Math.min(0.05 * index, 0.3),
                                }}
                                type="button"
                                onClick={() => {
                                  field.onChange(student.id);
                                  try {
                                    formContext.setValue(
                                      "payer_user_name" as any,
                                      student.full_name as any,
                                    );
                                    formContext.setValue(
                                      "payer_avatar_url" as any,
                                      (student.avatar_url ?? null) as any,
                                    );
                                  } catch (e) {}
                                }}
                              >
                                {student.avatar_url && (
                                  <img
                                    alt={student.full_name}
                                    className="absolute top-1/2 left-0 h-full -translate-y-1/2 rounded-lg object-cover opacity-30 blur-3xl"
                                    src={student.avatar_url}
                                  />
                                )}
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                                      field.value === student.id ? "bg-muted/50" : "bg-muted/50"
                                    }`}
                                  >
                                    {student.avatar_url ? (
                                      <img
                                        alt={student.full_name}
                                        className="h-full w-full rounded-lg object-cover"
                                        src={student.avatar_url}
                                      />
                                    ) : (
                                      <Icon
                                        className={
                                          field.value === student.id
                                            ? "text-foreground"
                                            : "text-muted-foreground"
                                        }
                                        height="1.25rem"
                                        icon="tabler:user"
                                        width="1.25rem"
                                      />
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h3
                                      className={`truncate text-sm font-semibold ${
                                        field.value === student.id
                                          ? "text-foreground"
                                          : "text-accent-foreground"
                                      }`}
                                    >
                                      {student.full_name}
                                    </h3>
                                  </div>

                                  {/* Selection indicator */}
                                  {field.value === student.id && (
                                    <motion.div
                                      animate={{ scale: 1, opacity: 1 }}
                                      className="absolute top-3 right-3"
                                      initial={{ scale: 0, opacity: 0 }}
                                    >
                                      <div className="bg-muted/50 flex h-5 w-5 items-center justify-center rounded-full">
                                        <Icon
                                          className="text-foreground"
                                          height="0.75rem"
                                          icon="tabler:check"
                                          width="0.75rem"
                                        />
                                      </div>
                                    </motion.div>
                                  )}
                                </div>
                              </motion.button>
                            ))}
                          </div>
                        </ScrollArea>
                      ) : (
                        <div className="text-muted-foreground flex h-40 flex-col items-center justify-center text-center">
                          <Icon
                            className="mx-auto mb-3 opacity-50"
                            height="2rem"
                            icon="tabler:users-off"
                            width="2rem"
                          />
                          <h3 className="text-accent-foreground mb-2 text-sm font-semibold">
                            {t("finances:createPayment.noStudents")}
                          </h3>
                          <p className="text-muted-foreground text-sm">
                            {t("finances:createPayment.noStudentsDesc")}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Payment Due Date */}
        <div className="space-y-4">
          <FormField
            control={control}
            name="payment_due"
            render={({ field }) => {
              const selectedDate = field.value ? parseISO(`${field.value}T00:00:00`) : null;

              return (
                <FormItem>
                  <FormLabel className="text-accent-foreground text-base font-medium">
                    {t("finances:createPayment.dueDate")}
                  </FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          className="bg-muted/30 focus:bg-muted/20 h-12 w-full justify-start rounded-xl text-base font-normal"
                          variant="outline"
                        >
                          <Icon
                            className="text-muted-foreground mr-3"
                            height="1.25rem"
                            icon="tabler:calendar"
                            width="1.25rem"
                          />
                          {selectedDate
                            ? format(selectedDate, "PPP", {
                                locale: getDateFnsLocale(i18n.language),
                              })
                            : t("common:pickDate")}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="z-[2000] w-auto p-0">
                      <Calendar
                        initialFocus
                        disabled={(date) => !isAfter(date, startOfToday())}
                        locale={getDateFnsLocale(i18n.language)}
                        mode="single"
                        selected={selectedDate ?? undefined}
                        weekStartsOn={1}
                        onSelect={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-muted-foreground text-sm">
                    {t("finances:createPayment.dueDateHelper")}
                  </p>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
